/**
 * src/pages/WhereIsEverythingGoing/futureData.ts
 * Rigorous cosmological data, timeline events, scenarios, horizons, and citations for "Where Is Everything Going?".
 */

import type { SourceRef, EvidenceLevel } from "@/lib/originBeyond/types";

/**
 * 9 Verified scholarly & archival citations
 */
export const FUTURE_SOURCES: SourceRef[] = [
  {
    id: 1,
    citation:
      "Schröder, K.-P., & Smith, R. C. (2008). Distant future of the Sun and Earth revisited. Monthly Notices of the Royal Astronomical Society, 386(1), 155–163.",
    url: "https://arxiv.org/abs/0801.4031",
  },
  {
    id: 2,
    citation:
      "Adams, F. C., & Laughlin, G. (1997). A dying universe: the long-term properties and fate of astrophysical objects. Reviews of Modern Physics, 69(2), 337–372.",
    url: "https://arxiv.org/abs/astro-ph/9701131",
  },
  {
    id: 3,
    citation:
      "Riess, A. G., et al. (Supernova Search Team). (1998). Observational evidence from supernovae for an accelerating universe and a cosmological constant. The Astronomical Journal, 116(3), 1009–1038.",
    url: "https://arxiv.org/abs/astro-ph/9805201",
  },
  {
    id: 4,
    citation:
      "Perlmutter, S., et al. (Supernova Cosmology Project). (1999). Measurements of Omega and Lambda from 42 high-redshift supernovae. The Astrophysical Journal, 517(2), 565–586.",
    url: "https://arxiv.org/abs/astro-ph/9812133",
  },
  {
    id: 5,
    citation:
      "Planck Collaboration. (2020). Planck 2018 results. VI. Cosmological parameters. Astronomy & Astrophysics, 641, A6.",
    url: "https://arxiv.org/abs/1807.06209",
  },
  {
    id: 6,
    citation:
      "van der Marel, R. P., Besla, G., Cox, T. J., Sohn, S. T., & Anderson, J. (2012). The M31 velocity vector. III. Future Milky Way M31-orbit, collision, and merger. The Astrophysical Journal, 753(1), 9.",
    url: "https://arxiv.org/abs/1205.6865",
  },
  {
    id: 7,
    citation:
      "DESI Collaboration. (2024). DESI 2024 VI: Cosmological Constraints from the Measurements of Baryon Acoustic Oscillations. arXiv preprint.",
    url: "https://arxiv.org/abs/2404.03002",
  },
  {
    id: 8,
    citation:
      "Kardashev, N. S. (1964). Transmission of Information by Extraterrestrial Civilizations. Soviet Astronomy, 8, 217; Sagan, C. (1973). The Cosmic Connection.",
    url: "https://en.wikipedia.org/wiki/Kardashev_scale",
  },
  {
    id: 9,
    citation:
      "Ord, T. (2020). The Precipice: Existential Risk and the Future of Humanity. Hachette Books.",
    url: "https://theprecipice.com",
  },
];

export interface HorizonClaim {
  id: string;
  title: string;
  level: EvidenceLevel;
  body: string;
  refs?: number[];
}

export interface HorizonItem {
  id: string;
  label: string;
  span: string;
  summary: string;
  claims: HorizonClaim[];
}

export const TIME_HORIZONS: HorizonItem[] = [
  {
    id: "h-100yr",
    label: "Next 100 Years",
    span: "Present to ~2125 CE",
    summary:
      "The near horizon is dominated by human technological acceleration, climate system inertia, and ecological stewardship.",
    claims: [
      {
        id: "c-100-climate",
        title: "Energy transition and climate trajectory",
        level: "research",
        body: "Emissions pathways chosen over the next few decades determine whether global mean temperature stabilizes near 1.5°C–2.0°C or triggers stronger polar feedback loops. Physical laws bound the warming, but human societal and policy choices remain unwritten.",
        refs: [9],
      },
      {
        id: "c-100-ai",
        title: "Machine intelligence and automated reasoning",
        level: "research",
        body: "Advanced artificial cognitive architectures are transforming scientific discovery, materials design, and global economics. Experts vigorously debate whether artificial general intelligence emerges soon or hits unforeseen computational bottlenecks.",
        refs: [9],
      },
      {
        id: "c-100-space",
        title: "Initial permanent footholds off Earth",
        level: "speculative",
        body: "Robotic resource prospecting and small crewed habitats on the Moon and Mars are technically plausible in this century. Establishing self-sustaining biosystems remains an open biophysical challenge.",
      },
    ],
  },
  {
    id: "h-10kyr",
    label: "Next 10,000 Years",
    span: "~10⁴ Years",
    summary:
      "Planetary orbital mechanics, human civilizational longevity, and deep-time geological records take centre stage.",
    claims: [
      {
        id: "c-10k-orbital",
        title: "Milankovitch orbital cycles and glaciation",
        level: "research",
        body: "Earth's axial tilt and orbital eccentricity follow predictable cycles of roughly 26,000 to 100,000 years. Elevated atmospheric carbon dioxide has likely delayed the onset of the next natural glacial maximum by tens of thousands of years.",
      },
      {
        id: "c-10k-language",
        title: "Linguistic and cultural drift",
        level: "research",
        body: "Over ten millennia, every modern spoken language will either evolve beyond mutual intelligibility or be preserved only through digital archives. Nuclear waste markers must communicate hazard across deep language barriers.",
      },
      {
        id: "c-10k-civilization",
        title: "Survival of high-energy civilization",
        level: "philosophy",
        body: "Whether industrial civilization can navigate existential bottlenecks and achieve long-term equilibrium is a core question of philosophical ethics and future studies.",
        refs: [9],
      },
    ],
  },
  {
    id: "h-1byr",
    label: "Next Billion Years",
    span: "~10⁹ Years",
    summary:
      "The physics of main-sequence stellar evolution steadily transforms the inner Solar System and Earth's biosphere.",
    claims: [
      {
        id: "c-1b-sun",
        title: "Solar core fusion and luminosity increase",
        level: "established",
        body: "As hydrogen fuses into denser helium ash in the Sun's core, core pressure and temperature slowly climb. The Sun brightens by roughly 10% every billion years, a well-established consequence of stellar equilibrium equations.",
        refs: [1],
      },
      {
        id: "c-1b-oceans",
        title: "Evaporation of Earth's oceans and moist greenhouse",
        level: "research",
        body: "In roughly 1 to 1.5 billion years, increased solar irradiance will accelerate ocean evaporation. Water vapor in the stratosphere will be dissociated by ultraviolet sunlight, leading to irreversible hydrogen escape to space.",
        refs: [1],
      },
      {
        id: "c-1b-andromeda",
        title: "Andromeda–Milky Way gravitational interaction",
        level: "research",
        body: "Hubble measurements indicated an impending collision in roughly 4 to 5 billion years (van der Marel et al. 2012). Recent orbital simulations with updated Gaia and satellite dwarf data note the merger probability in 10 Gyr is roughly 50%, depending on dark matter halos.",
        refs: [6],
      },
    ],
  },
  {
    id: "h-100byr",
    label: "Next 100 Billion Years",
    span: "~10¹¹ Years",
    summary:
      "Cosmic expansion accelerates, isolating our local galaxy group into a single island in an otherwise unreachable void.",
    claims: [
      {
        id: "c-100b-milkomeda",
        title: "Formation of the Milkomeda super-elliptical galaxy",
        level: "established",
        body: "Regardless of exact collision timing, the gravitationally bound galaxies of our Local Group will eventually coalesce into a single merged elliptical galaxy, often informally called Milkomeda.",
        refs: [2, 6],
      },
      {
        id: "c-100b-cosmic-horizon",
        title: "Cosmological event horizon and isolation",
        level: "established",
        body: "Because metric space expansion is accelerating, distant galaxy clusters will cross outside our cosmological event horizon. Their emitted light will redshift to wavelengths longer than the observable universe, leaving our local group visibly isolated.",
        refs: [3, 4, 5],
      },
      {
        id: "c-100b-last-stars",
        title: "Exhaustion of star-forming nebulae",
        level: "research",
        body: "Interstellar gas will be progressively locked away in white dwarfs, neutron stars, and black holes. Star formation rates in galaxies will steadily drop toward zero.",
        refs: [2],
      },
    ],
  },
  {
    id: "h-10to14",
    label: "Beyond 10¹⁴ Years",
    span: "10¹⁴ to 10¹⁰⁰+ Years",
    summary:
      "The stelliferous era ends; reality enters an extraordinarily slow transition through degenerate matter and black hole evaporation.",
    claims: [
      {
        id: "c-1014-red-dwarfs",
        title: "Extinction of the lowest-mass red dwarfs (~10¹⁴ yr)",
        level: "established",
        body: "The smallest red dwarf stars (roughly 0.08 solar masses) sip their hydrogen fuel so efficiently that they can shine for 10 to 12 trillion years. Around 10¹⁴ years, the very last natural nuclear stars will burn out.",
        refs: [2],
      },
      {
        id: "c-1014-proton-decay",
        title: "Hypothetical proton decay (~10³⁴ to 10⁴⁰ yr)",
        level: "speculative",
        body: "Grand Unified Theories predict that protons may decay into positrons and pions with half-lives exceeding 10³⁴ years. However, underground detectors such as Super-Kamiokande have observed zero proton decay events to date, leaving this unconfirmed.",
        refs: [2],
      },
    ],
  },
];

export interface SunStage {
  ageGyr: number;
  label: string;
  stageName: string;
  radiusSolar: number;
  luminositySolar: number;
  tempK: number;
  colorClass: string;
  earthHabitability: string;
  earthNotes: string;
  level: EvidenceLevel;
}

export const SUN_LIFECYCLE_STAGES: SunStage[] = [
  {
    ageGyr: 0.0,
    label: "Zero-Age Main Sequence",
    stageName: "Birth of Solar System",
    radiusSolar: 0.9,
    luminositySolar: 0.7,
    tempK: 5600,
    colorClass: "from-amber-300 to-yellow-500",
    earthHabitability: "Prebiotic / Molten",
    earthNotes:
      "The young Sun is about 30% dimmer than today. Earth's early atmosphere was warmed by potent greenhouse gases (faint young Sun problem) as oceans condensed.",
    level: "established",
  },
  {
    ageGyr: 4.6,
    label: "Present Day (~4.6 Gyr)",
    stageName: "Middle-Aged Main Sequence",
    radiusSolar: 1.0,
    luminositySolar: 1.0,
    tempK: 5778,
    colorClass: "from-yellow-200 to-amber-400",
    earthHabitability: "Thriving Biosphere",
    earthNotes:
      "The Sun steadily fuses roughly 600 million tons of hydrogen into helium every second. Surface temperatures support liquid water oceans and a rich planetary biosphere.",
    level: "established",
  },
  {
    ageGyr: 6.0,
    label: "Future Main Sequence (+1.4 Gyr)",
    stageName: "Brightening Sun",
    radiusSolar: 1.15,
    luminositySolar: 1.15,
    tempK: 5820,
    colorClass: "from-yellow-100 to-amber-300",
    earthHabitability: "Oceans Evaporating",
    earthNotes:
      "Solar luminosity increases by ~10% over current levels. Models show runaway or moist greenhouse conditions: oceans evaporate, water vapor is split by UV, and hydrogen escapes.",
    level: "research",
  },
  {
    ageGyr: 10.0,
    label: "Subgiant Branch (~10 Gyr)",
    stageName: "Core Hydrogen Exhaustion",
    radiusSolar: 2.1,
    luminositySolar: 2.8,
    tempK: 5000,
    colorClass: "from-orange-400 to-amber-600",
    earthHabitability: "Sterilized Desolation",
    earthNotes:
      "Hydrogen fusion shifts into a shell around the inert helium core. Earth's surface is molten rock under extreme solar radiation; complex life is long extinct.",
    level: "established",
  },
  {
    ageGyr: 11.5,
    label: "Red Giant Peak (~11.6 Gyr)",
    stageName: "First Red Giant Branch",
    radiusSolar: 180,
    luminositySolar: 2700,
    tempK: 3100,
    colorClass: "from-red-500 to-orange-600",
    earthHabitability: "Engulfment Probable",
    earthNotes:
      "The Sun expands beyond 1 AU. Although the Sun loses ~33% of its mass (widening Earth's orbit), tidal drag from the extended chromosphere makes engulfment probable (Schröder & Smith 2008).",
    level: "research",
  },
  {
    ageGyr: 12.0,
    label: "Planetary Nebula & White Dwarf",
    stageName: "White Dwarf Remnant",
    radiusSolar: 0.01,
    luminositySolar: 0.001,
    tempK: 25000,
    colorClass: "from-cyan-200 to-blue-400",
    earthHabitability: "Frozen Ash / Destroyed",
    earthNotes:
      "Outer gas layers drift into space as a luminous planetary nebula. The degenerate carbon-oxygen core cools across trillions of years as a white dwarf.",
    level: "established",
  },
];

export interface CosmicScenario {
  id: string;
  name: string;
  title: string;
  evidence: EvidenceLevel;
  badgeText: string;
  equationState: string;
  description: string;
  mechanism: string;
  timeframe: string;
  statusText: string;
  accentClass: string;
}

export const COSMIC_SCENARIOS: CosmicScenario[] = [
  {
    id: "heat-death",
    name: "Heat Death / Big Freeze",
    title: "Maximum Entropy in Endless Expansion",
    evidence: "research",
    badgeText: "Leading Scientific Model",
    equationState: "w = −1.0 (Cosmological Constant Λ)",
    description:
      "If dark energy remains a true cosmological constant (equation of state parameter w = −1), the metric expansion of space continues forever at an accelerating rate. Stars eventually exhaust their nuclear fuel, galaxies drift out of mutual contact, and the cosmos approaches thermodynamic equilibrium at fractions of a Kelvin above absolute zero.",
    mechanism:
      "Continued cosmic dilution of matter and radiation, with dark energy density remaining constant per unit volume.",
    timeframe: "10¹⁴ to 10¹⁰⁰+ years",
    statusText:
      "Currently best supported by Planck 2018 cosmic microwave background measurements and Type Ia supernova observations.",
    accentClass: "border-primary/50 text-primary",
  },
  {
    id: "big-rip",
    name: "Big Rip",
    title: "Exponential Acceleration Tearing Matter Apart",
    evidence: "speculative",
    badgeText: "Phantom Energy Consequence",
    equationState: "w < −1.0 (Phantom Energy)",
    description:
      "If dark energy strengthens over time (phantom energy with w < −1), cosmic acceleration diverges to infinity in finite time. The repulsive force would eventually overcome gravitational binding in galaxy clusters, stellar orbits, planetary bodies, and finally tear atoms and atomic nuclei apart at a terminal singularity.",
    mechanism:
      "Dark energy density increases with cosmic expansion rather than staying constant or decreasing.",
    timeframe: "Roughly 20 to 50 billion years from now",
    statusText:
      "Speculative hypothesis; current observational constraints keep w very close to −1.0, but uncertainties leave slight phantom regimes technically unruled out.",
    accentClass: "border-rose-500/50 text-rose-400",
  },
  {
    id: "big-crunch",
    name: "Big Crunch",
    title: "Gravitational Re-Collapse to a Hot Singularity",
    evidence: "research",
    badgeText: "Disfavoured by Current Acceleration",
    equationState: "w > −1/3 or Dark Energy Becomes Attractive",
    description:
      "In early cosmology, if total matter density exceeded the critical density, universal expansion would eventually halt and reverse, collapsing all matter and radiation into a catastrophic high-energy Big Crunch singularity.",
    mechanism:
      "Gravitational attraction overcomes kinetic expansion, requiring dark energy to decay or switch sign.",
    timeframe: "Dozens of billions of years (if initiated)",
    statusText:
      "Disfavoured by the 1998 discovery of cosmic acceleration (Riess et al.; Perlmutter et al.), though non-zero probability remains if dynamical dark energy (quintessence) decays into a negative potential field.",
    accentClass: "border-amber-500/50 text-amber-400",
  },
  {
    id: "vacuum-decay",
    name: "Vacuum Decay",
    title: "Metastable Higgs Field Tunneling to True Vacuum",
    evidence: "speculative",
    badgeText: "Quantum Field Catastrophe",
    equationState: "Higgs Potential V(φ) < V_present",
    description:
      "Precision measurements of the top quark mass and Higgs boson mass suggest our universe might reside in a metastable 'false vacuum'. Quantum tunneling through the potential barrier could nucleate a bubble of 'true vacuum' that expands outward at the speed of light, rewriting the laws of physics and mass inside.",
    mechanism:
      "First-order quantum phase transition via instanton tunneling in the electroweak Higgs effective potential.",
    timeframe: "Expected lifetime >> 10¹⁰⁰ years, or stochastic",
    statusText:
      "Theoretically sound within the Standard Model without new physics, but completely unobserved. Unknown Planck-scale physics could stabilize the vacuum.",
    accentClass: "border-purple-500/50 text-purple-400",
  },
  {
    id: "big-bounce",
    name: "Big Bounce / Cyclic Universe",
    title: "Periodic Contraction and Re-Expansion",
    evidence: "speculative",
    badgeText: "Loop Quantum Gravity / Brane Collisions",
    equationState: "Non-singular quantum geometry rebound",
    description:
      "Models incorporating loop quantum cosmology or higher-dimensional brane collisions (ekpyrotic universe) suggest singularities are avoided by quantum repulsive effects at the Planck density. In these frameworks, the collapse of a prior cosmos rebounds into a new expansion.",
    mechanism:
      "Quantum geometry minimum area gap preventing infinite density collapse, generating repulsive Planckian forces.",
    timeframe: "Infinite recurrent cycles",
    statusText:
      "Fascinating theoretical alternative with no empirical confirmation to date. Generating observed cosmic microwave background perturbations without fine-tuning remains challenging.",
    accentClass: "border-blue-500/50 text-blue-400",
  },
];

export interface CosmicEra {
  id: string;
  name: string;
  rangeLog: string;
  timeRange: string;
  level: EvidenceLevel;
  description: string;
  keyPhenomena: string[];
  refs?: number[];
}

export const COSMIC_ERAS: CosmicEra[] = [
  {
    id: "stelliferous",
    name: "Stelliferous Era",
    rangeLog: "10⁶ to 10¹⁴ yr",
    timeRange: "Now to roughly 100 trillion years",
    level: "established",
    description:
      "The era of active stars, fusion furnaces, and planetary systems. Most energy in the universe is generated in the nuclear cores of stars. Over this time, interstellar gas is continuously locked into white dwarfs, neutron stars, and black holes until no new stars can be formed.",
    keyPhenomena: [
      "Nuclear fusion in main sequence and red dwarf stars",
      "Formation of complex heavy elements via supernovae and neutron star mergers",
      "Gradual exhaustion of molecular hydrogen gas clouds in galaxies",
    ],
    refs: [2],
  },
  {
    id: "degenerate",
    name: "Degenerate Era",
    rangeLog: "10¹⁴ to 10⁴⁰ yr",
    timeRange: "100 trillion to 10⁴⁰ years",
    level: "research",
    description:
      "The universe is populated only by dead stellar remnants: brown dwarfs, cold white dwarfs (black dwarfs), neutron stars, and black holes. The primary energy sources are slow gravitational encounters, rare collision of dead stars, and hypothetical proton decay.",
    keyPhenomena: [
      "Dynamical relaxation: galaxies eject 90% of stellar corpses into intergalactic space",
      "Remaining 10% fall into supermassive central black holes",
      "If protons decay (~10³⁴ to 10⁴⁰ yr), matter evaporates into radiation; if stable, black dwarfs persist",
    ],
    refs: [2],
  },
  {
    id: "black-hole",
    name: "Black Hole Era",
    rangeLog: "10⁴⁰ to 10¹⁰⁰ yr",
    timeRange: "10⁴⁰ to 10¹⁰⁰ years",
    level: "research",
    description:
      "Once all baryonic matter has either fallen into black holes or decayed (if proton decay occurs), black holes are the only significant organized structures remaining. Over unfathomable timescales, they slowly radiate away their mass via Hawking radiation.",
    keyPhenomena: [
      "Stellar-mass black holes evaporate around 10⁶⁶ years",
      "Supermassive black holes of 10¹¹ solar masses evaporate around 10¹⁰⁰ years",
      "Final explosive flash of high-energy gamma rays and particles as each black hole finishes evaporation",
    ],
    refs: [2],
  },
  {
    id: "dark",
    name: "Dark Era",
    rangeLog: "> 10¹⁰⁰ yr",
    timeRange: "Beyond 10¹⁰⁰ years into eternity",
    level: "speculative",
    description:
      "Only an extraordinarily dilute gas of photons, neutrinos, electrons, and positrons remains in an unimaginably vast, near-empty cosmos. Temperatures sit infinitesimally above absolute zero. At these scales, physical events are separated by light-years of distance and eons of time.",
    keyPhenomena: [
      "Occasional formation of positronium (bound electron-positron pairs) with orbital radii larger than the observable universe",
      "Spontaneous quantum fluctuations on Poincaré recurrence timescales (> 10^(10^26) years)",
      "Near-complete cosmic silence at maximum physical entropy",
    ],
    refs: [2],
  },
];
